import React, { useState, useEffect } from 'react';
import { FileText, FileCode, Download, AlertCircle, Loader2, Image as ImageIcon, FileSpreadsheet, Eye } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

const FileViewer = ({ url, title, type, description, allowHtmlPdfDownload = false, onComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [htmlSource, setHtmlSource] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const onCompleteRef = React.useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const getFileType = React.useCallback((url, type) => {
    if (!url || url === '#') return 'unknown';
    const extension = url.split('.').pop().split(/[?#]/)[0].toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    if (extension === 'pdf') return 'pdf';
    if (['html', 'htm'].includes(extension)) return 'html';
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension)) return 'office';
    if (['txt', 'md', 'csv'].includes(extension)) return 'text';
    
    // Fallback to type if extension is not clear
    const lowerType = type?.toLowerCase() || '';
    const lowerUrl = url.toLowerCase();

    if (lowerType.includes('imagem') || lowerUrl.includes('image')) return 'image';
    if (lowerType.includes('pdf') || lowerUrl.includes('.pdf') || lowerUrl.endsWith('pdf')) return 'pdf';
    if (lowerType.includes('html') || lowerUrl.includes('.html') || lowerUrl.includes('.htm')) return 'html';
    if (lowerType.includes('planilha') || lowerType.includes('documento') || lowerUrl.includes('.docx') || lowerUrl.includes('.xlsx') || lowerUrl.includes('.xls')) return 'office';
    
    return 'unknown';
  }, []);

  const fileType = React.useMemo(() => getFileType(url, type), [url, type, getFileType]);

  useEffect(() => {
    if (fileType !== 'html') {
      setHtmlSource('');
      return;
    }
    if (!url || url === '#') {
      setLoading(false);
      setError(true);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);
    setHtmlSource('');

    const loadHtml = async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        if (!cancelled) {
          setHtmlSource(html || '');
          setLoading(false);
          onCompleteRef.current?.();
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadHtml();

    return () => {
      cancelled = true;
    };
  }, [fileType, url]);

  useEffect(() => {
    if (fileType === 'html') return;
    if (url && url !== '#') {
      setLoading(true);
      setError(false);

      // Se for um tipo que não dispara onLoad (fallback), removemos o loading após um curto período
      if (fileType === 'unknown') {
        const timer = setTimeout(() => setLoading(false), 600);
        return () => clearTimeout(timer);
      }

      // Timeout de segurança para evitar carregamento infinito
      const safetyTimer = setTimeout(() => {
        setLoading(false);
        // Se ainda estiver carregando após 5s, assumimos que pode ter falhado silenciosamente (ex: Google Viewer)
        // Não setamos erro, mas o loading sai para mostrar o conteúdo (que pode ser a msg de erro do Google)
      }, 5000);

      return () => clearTimeout(safetyTimer);
    } else {
      setLoading(false);
      if (url === '#') setError(true);
    }
  }, [url, fileType]);

  const renderContent = () => {
    if (!url || url === '#') return null;

    switch (fileType) {
      case 'image':
        return (
          <div className="flex flex-col items-center justify-center w-full h-full p-4 overflow-auto custom-scrollbar">
            <img 
              src={url} 
              alt={title} 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onLoad={() => { setLoading(false); onComplete?.(); }}
              onError={() => { setLoading(false); setError(true); }}
            />
          </div>
        );
      case 'pdf':
        // Verifica se é localhost para decidir a estratégia
        const isLocal = url.includes('localhost') || url.includes('127.0.0.1');
        
        return (
          <div className="w-full h-full bg-slate-100 flex flex-col relative group">
            {/* Mensagem de Fallback Centralizada - Visível se o embed falhar ou demorar */}
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center z-0">
               <FileText size={48} className="text-slate-300 mb-4" />
               <p className="text-slate-500 font-medium mb-4">Carregando visualizador...</p>
               <a 
                 href={url} 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg font-bold shadow-lg transition flex items-center gap-2"
               >
                 <Download size={18} /> Baixar / Abrir em Nova Aba
               </a>
               <p className="text-xs text-slate-400 mt-4 max-w-xs">
                 Se o documento não aparecer automaticamente, utilize o botão acima.
               </p>
            </div>

            {/* Tentativa de Embed Nativo - Z-Index maior para cobrir o fallback se carregar */}
            <embed
              src={url}
              type="application/pdf"
              className="w-full h-full relative z-10"
              onLoad={() => { setLoading(false); onComplete?.(); }}
              onError={() => { setLoading(false); setError(true); }}
            />
          </div>
        );
      case 'office':
        return (
          <div className="w-full h-full bg-slate-100 flex flex-col items-center justify-center p-6 text-center">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 max-w-md w-full">
              <FileSpreadsheet size={48} className="text-emerald-600 mb-4 mx-auto" />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Visualização de Documento</h3>
              <p className="text-slate-500 mb-6">
                Este arquivo Office pode ser visualizado online ou baixado para seu dispositivo.
              </p>
              
              <div className="flex flex-col gap-3">
                <a 
                  href={`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full bg-slate-800 hover:bg-slate-700 text-white px-6 py-3 rounded-lg font-bold transition flex items-center justify-center gap-2"
                >
                  <Eye size={18} /> Visualizar Online
                </a>
                
                <a 
                  href={url}
                  download
                  className="w-full bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-lg font-bold transition flex items-center justify-center gap-2"
                >
                  <Download size={18} /> Baixar Arquivo
                </a>
              </div>
            </div>
          </div>
        );
      case 'text':
        return (
          <iframe
            src={url}
            className="w-full h-full border-0 rounded-lg bg-slate-950 text-slate-100 p-4 font-mono"
            onLoad={() => { setLoading(false); onComplete?.(); }}
            onError={() => { setLoading(false); setError(true); }}
            title={title}
          />
        );
      case 'html':
        return (
          <iframe
            srcDoc={htmlSource}
            className="w-full h-full border-0 rounded-lg bg-white"
            title={title}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          />
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center text-center p-12 h-full">
            <div className="w-24 h-24 bg-slate-800 rounded-2xl flex items-center justify-center mb-6 shadow-xl border border-slate-700">
               <FileText size={48} className="text-teal-500 opacity-40" />
            </div>
            <h3 className="text-2xl font-black text-white mb-4 tracking-tight">Visualização não disponível</h3>
            <p className="text-slate-400 mb-8 max-w-md font-medium">
              Este tipo de arquivo não pode ser visualizado diretamente no navegador. Por favor, faça o download para visualizar localmente.
            </p>
            <a
              href={url}
              download
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onComplete?.()}
              className="bg-teal-500 hover:bg-teal-400 text-slate-950 px-8 py-3 rounded-xl font-black flex items-center gap-3 transition shadow-lg shadow-teal-500/20 hover:scale-[1.02] active:scale-95"
            >
              <Download size={20} />
              BAIXAR MATERIAL
            </a>
          </div>
        );
    }
  };

  const handleDownloadHtmlAsPdf = async () => {
    if (!htmlSource) return;
    setDownloadingPdf(true);
    try {
      const safeTitle = String(title || 'conteudo')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9_-]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase() || 'conteudo';

      const { data: { session } } = await supabase.auth.getSession();
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pdfshift`;
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          html_source: htmlSource,
          file_name: safeTitle,
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Erro ao gerar PDF no servidor');
      }

      const blob = await response.blob();
      const pdfUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = pdfUrl;
      a.download = `${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(pdfUrl);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Erro ao gerar PDF do HTML:', err);
      alert(`Não foi possível gerar o PDF deste conteúdo HTML. ${err?.message ? `(${err.message})` : ''}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-slate-950 relative overflow-hidden rounded-xl border border-slate-800/50 shadow-2xl">
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950/90 backdrop-blur-sm z-20">
          <div className="relative">
             <div className="w-16 h-16 border-4 border-teal-500/20 border-t-teal-500 rounded-full animate-spin"></div>
             <div className="absolute inset-0 flex items-center justify-center">
                <FileText size={20} className="text-teal-500 animate-pulse" />
             </div>
          </div>
          <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px] mt-6 animate-pulse">Carregando Material Didático...</p>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-950 z-20 p-8 text-center">
          <div className="w-20 h-20 bg-rose-500/10 border border-rose-500/20 rounded-full flex items-center justify-center mb-6">
             <AlertCircle size={40} className="text-rose-500" />
          </div>
          <h3 className="text-2xl font-black text-white mb-2 tracking-tight">Erro ao carregar arquivo</h3>
          <p className="text-slate-400 mb-8 max-w-sm font-medium">
            Não foi possível carregar o arquivo solicitado. Ele pode estar indisponível no momento ou o formato não é suportado pelo seu navegador.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
             <button 
               onClick={() => { setError(false); setLoading(true); }}
               className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-xl font-bold transition shadow-lg border border-slate-700"
             >
               Tentar Novamente
             </button>
             {fileType !== 'html' && (
               <a
                href={url}
                download
                className="bg-teal-500 hover:bg-teal-400 text-slate-950 px-8 py-3 rounded-xl font-black transition shadow-lg shadow-teal-500/20"
              >
                Baixar Agora
              </a>
             )}
          </div>
        </div>
      )}

      <div className="flex-1 w-full min-h-0 relative bg-slate-950">
        {renderContent()}
      </div>
      
      {/* File Info Bar - Integrated with player style */}
      <div className="p-4 px-6 border-t border-slate-800/50 bg-slate-900/40 backdrop-blur-md flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-slate-950/50 rounded-xl text-teal-500 border border-teal-500/20 shadow-inner">
            {fileType === 'image' ? <ImageIcon size={20} /> : 
             fileType === 'pdf' ? <FileText size={20} /> : 
             fileType === 'html' ? <FileCode size={20} /> :
             fileType === 'office' ? <FileSpreadsheet size={20} /> : <FileText size={20} />}
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-black text-white truncate max-w-[200px] md:max-w-md tracking-tight uppercase">{title}</h4>
            <div className="flex items-center gap-2">
               <span className="text-[9px] text-teal-500 font-black uppercase tracking-widest">{type || 'Documento'}</span>
               <span className="w-1 h-1 rounded-full bg-slate-700"></span>
               <span className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Material Complementar</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
           {fileType === 'html' && allowHtmlPdfDownload && (
             <button
               type="button"
               onClick={handleDownloadHtmlAsPdf}
               disabled={downloadingPdf || !htmlSource}
               className="px-3 py-2 text-xs font-bold text-slate-200 hover:text-white hover:bg-slate-800 rounded-xl transition-all border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
               title="Baixar conteúdo HTML em PDF"
             >
               {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
             </button>
           )}
           {fileType !== 'html' && (
             <a
               href={url}
               download
               target="_blank"
               rel="noopener noreferrer"
               onClick={() => onComplete?.()}
               className="p-3 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-all hover:scale-105 active:scale-95 border border-transparent hover:border-slate-700"
               title="Baixar arquivo"
             >
               <Download size={20} />
             </a>
           )}
        </div>
      </div>
    </div>
  );
};

export default FileViewer;
